/**
 * TND-008 / C181-bis — la liste des pièces d'une consultation, PAR PAGES.
 *
 * ## 🔴 Ce qui manquait : la suite
 *
 * Le serveur borne la liste (`DOCUMENTS_PAR_PAGE`, 200) et le DIT
 * (`documents_has_more`) depuis le 2026-09-21. Mais l'écran ne savait pas aller
 * chercher la suite : l'onglet Documents renvoyait vers le filtre par dossier et
 * la recherche, deux détours, et aucun pour une pièce dont on ignore le nom. Le
 * serveur rend désormais `documents_next_cursor` (keyset `(created_at, id)`,
 * `services/tender_documents_curseur.py`), que la page suivante renvoie en
 * `cursor`.
 *
 * ## Une requête INFINIE, lue comme une réponse UNIQUE
 *
 * `useTenderDocuments` a cinq lecteurs ; un seul (l'onglet Documents) demande la
 * suite. `select` (`aplatirLesPages`) rend donc aux cinq la forme qu'ils lisaient
 * déjà — `documents`, `folders`, `documents_has_more` — et seul l'onglet lit en
 * plus `hasNextPage` / `fetchNextPage`. Changer la forme de `data` aurait cassé
 * les quatre autres pour un geste qu'ils n'offrent pas.
 *
 * ⚠️ `aplatirLesPages` vit au niveau du MODULE, jamais en lambda : react-query ne
 * rejoue `select` que si les pages OU la référence de la fonction changent. En
 * lambda, l'aplatissement (deux cents lignes et plus) se referait à chaque rendu
 * — le partage structurel rendrait la même référence, mais après tout recalculé.
 *
 * ⚠️ `documents_has_more` est celui de la DERNIÈRE page chargée : c'est lui qui
 * dit s'il en reste au-delà de ce que l'écran montre. Celui de la première dirait
 * « il en reste » après que tout a été chargé.
 */

import { infiniteQueryOptions, keepPreviousData, type InfiniteData } from '@tanstack/react-query'

import { tendersKeys } from '@/lib/queryKeys.tenders'

import { TENDERS_ENDPOINT, type TenderDocumentsPayload } from './useTenders.types'

/** Une page telle que la route la rend : la réponse, plus où reprendre. */
export type PageDeDocuments = TenderDocumentsPayload & {
    /** Le curseur de la page suivante — `null` (ou absent) : c'était la dernière. */
    documents_next_cursor?: string | null
}

export type FiltresDesDocuments = { folder_id?: string | null; search?: string | null }

/** L'URL d'une page. `URLSearchParams` ENCODE le curseur : il porte `:` et `|`. */
export function urlDesDocuments(
    tenderId: string,
    entityId: string,
    filtres: FiltresDesDocuments | undefined,
    curseur: string | null,
): string {
    const params = new URLSearchParams({ entity_id: entityId })
    if (filtres?.folder_id) params.set('folder_id', filtres.folder_id)
    if (filtres?.search) params.set('search', filtres.search)
    if (curseur) params.set('cursor', curseur)
    return `${TENDERS_ENDPOINT}/tenders/${tenderId}/documents?${params}`
}

/** Où reprendre après `page` — `undefined` arrête `hasNextPage`. */
export function curseurSuivant(page: PageDeDocuments): string | undefined {
    return page.documents_next_cursor ?? undefined
}

/** Les pages chargées, lues comme UNE réponse : la forme qu'attendent les cinq lecteurs. */
export function aplatirLesPages(
    donnees: InfiniteData<PageDeDocuments, string | null>,
): TenderDocumentsPayload {
    const premiere = donnees.pages[0]
    const derniere = donnees.pages[donnees.pages.length - 1]
    return {
        // L'arborescence et les catégories sont les mêmes à chaque page : la
        // première fait foi, rechargée avec les autres à chaque invalidation.
        folders: premiere?.folders ?? [],
        deposit_categories: premiere?.deposit_categories ?? [],
        documents: donnees.pages.flatMap((page) => page.documents ?? []),
        documents_has_more: derniere?.documents_has_more === true,
    }
}

/**
 * Les options de la requête. `lire` est le `getJson` du module des crochets,
 * passé en argument pour ne pas importer `useTenders.ts` d'ici (import circulaire).
 */
export function optionsDesDocuments(
    tenderId: string | null,
    entityId: string,
    filtres: FiltresDesDocuments | undefined,
    lire: <T>(url: string) => Promise<T>,
) {
    return infiniteQueryOptions({
        queryKey: tendersKeys.tenders.documents(tenderId ?? '', filtres as never),
        queryFn: ({ pageParam }) =>
            lire<PageDeDocuments>(urlDesDocuments(tenderId ?? '', entityId, filtres, pageParam)),
        initialPageParam: null as string | null,
        getNextPageParam: curseurSuivant,
        select: aplatirLesPages,
        enabled: Boolean(tenderId && entityId),
        /* 🔴 **Les filtres font partie de la CLÉ** : changer de dossier — ou taper
           une lettre dans la recherche — fabrique une clé neuve, donc
           `data === undefined` et `isLoading === true` le temps de l'aller-retour.
           L'onglet Documents rend un `Skeleton` dans ce cas : le champ de
           recherche était DÉMONTÉ à chaque frappe débouncée, et le focus partait
           avec lui — impossible d'écrire un second mot.

           `keepPreviousData` garde la réponse précédente pendant la nouvelle
           requête : plus de démontage, et plus de clignotement de la barre de
           dossiers au changement de filtre. Sans effet sur les quatre autres
           lecteurs, dont la clé ne bouge pas (aucun filtre) ou passe de
           `enabled: false` à `enabled: true`, cas où il n'y a rien à garder.
           ⚠️ Changer de filtre repart de la PREMIÈRE page : les pages de la clé
           précédente ne se mélangent jamais à celles de la nouvelle. */
        placeholderData: keepPreviousData,
    })
}
